import {
  Assistant,
  AvTimer,
  CheckCircleOutline,
  FiberManualRecord,
  NotInterested,
  PauseCircleOutline,
  PlayCircleOutline,
  WarningTwoTone,
} from "@clockwise/design-system/icons";
import { ActionTypeEnum, AutopilotHistoryActionLog, StatusEnum } from "@clockwise/schema";
import classNames from "classnames";
import { findLastIndex } from "lodash";
import * as React from "react";
import { EventAutopilotStatusCardStatus } from "../../util/event-history.util";
import { Timeline } from "../timeline";
import { TimelineLog, TimelineLogType } from "../timeline/timeline";

export interface ClockwiseHistoryProps {
  clockwiseHistoryStatus: StatusEnum;
  actions: AutopilotHistoryActionLog[];
  eventAutopilotStatus: EventAutopilotStatusCardStatus | null;
  trackTimelineExpanded: (payload: Record<string, unknown>) => void;
  hasConflict?: boolean;
  futureLogs?: TimelineLog[];
}

type TimelineIconByActionType = {
  [Property in ActionTypeEnum]: typeof Assistant; // This makes typing easier instead of trying to import type defined deep within mui
};

const TimelineIconByAction: TimelineIconByActionType = {
  NextAutopilotRun: AvTimer,
  EventCreated: FiberManualRecord,
  AutopilotTurnedOn: Assistant,
  AutopilotTurnedOff: NotInterested,
  ClockwiseMeetingMove: CheckCircleOutline,
  EventRescheduled: CheckCircleOutline,
  ClockwiseMovePaused: PauseCircleOutline,
  ClockwiseMoveUnpaused: PlayCircleOutline,
  ClockwiseCouldNotResolveConflict: WarningTwoTone,
};

const getTimelineIcon = (action: ActionTypeEnum, showWarning: boolean) => {
  const Icon = TimelineIconByAction[action];
  return (
    <Icon
      className={classNames("cw-w-4 cw-h-4", {
        "cw-text-warning": showWarning,
      })}
    />
  );
};

const DefragInfoCard = ({ actionInfo }: { actionInfo: string[] }) => {
  return (
    <div className="cw-bg-neutral cw-px-2 cw-py-1 cw-my-2">
      {actionInfo.map((value) => (
        <div className="cw-caption cw-text-muted" key={value}>
          {value}
        </div>
      ))}
    </div>
  );
};

const HistoryTitle = () => (
  <div className="cw-heading-base cw-mt-4 cw-mb-2" cw-id="flexible-meeting-history-section">
    History
  </div>
);

export const ClockwiseHistory = ({
  actions,
  clockwiseHistoryStatus,
  trackTimelineExpanded,
  eventAutopilotStatus,
  hasConflict,
  futureLogs = [],
}: ClockwiseHistoryProps) => {
  if (!eventAutopilotStatus || clockwiseHistoryStatus === StatusEnum.AutopilotHistoryNotVisible) {
    return null;
  }

  // render an empty state if there is no AP history
  if (clockwiseHistoryStatus === StatusEnum.AutopilotHistoryOff) {
    return (
      <div>
        <HistoryTitle />
        <div className="cw-space-y-1 cw-border cw-border-solid cw-border-default cw-rounded cw-p-2">
          <div className="cw-body-sm cw-font-bold">
            History isn’t available for this event because it has never been managed by Clockwise
          </div>
          <div className="cw-caption">
            When an event is made flexible, event modifications will display here.
          </div>
        </div>
      </div>
    );
  }

  const mostRecentInstanceOfClockwiseCouldNotResolveConflict = findLastIndex(
    actions,
    (action) => action.actionType == ActionTypeEnum.ClockwiseCouldNotResolveConflict,
  );

  const historicalLogs = actions.map(
    ({ actionType, actionString, actionInfo, timestamp }, index) => {
      const isMostRecentCouldNotResolveConflict =
        index == mostRecentInstanceOfClockwiseCouldNotResolveConflict;
      const showWarning =
        isMostRecentCouldNotResolveConflict &&
        !!hasConflict &&
        actionType == ActionTypeEnum.ClockwiseCouldNotResolveConflict;

      const defragInfo =
        actionInfo && actionInfo.length ? <DefragInfoCard actionInfo={actionInfo} /> : null;

      return {
        timestamp,
        defragInfo,
        // hopefully this is unique enough....
        id: `${actionType}-${timestamp}`,
        description: actionString,
        type: TimelineLogType.PastLog,
        icon: getTimelineIcon(actionType, showWarning),
      };
    },
  );

  const timelineLogs = [...historicalLogs, ...futureLogs];

  return (
    <div>
      <HistoryTitle />
      <Timeline
        timelineLogs={timelineLogs}
        isMissingHistory={clockwiseHistoryStatus === StatusEnum.AutopilotHistoryIncomplete}
        trackTimelineExpanded={trackTimelineExpanded}
      />
    </div>
  );
};
